import {
	AttributeOption,
	CostUnit,
	Metadata,
	NODE_TYPES,
	Position,
	ReactFlowSchema,
	RehabAction,
	Rules,
	UnitMetadata,
} from '@Types';
import * as uuid from 'uuid';
import { createStartNode } from './TreeUtils';
import { TFunc } from '@Translations/types';

const xStart = 150;
const yStart = 50;
const xOffset = 250;
const yOffset = 120;

export const getAttributeLabel = (
	range: boolean,
	t: TFunc,
	getSystemValueFormatted: (
		value: string,
		invalidValue?: string,
		decimalPlaces?: number,
		standardUnit?: string,
	) => string | undefined,
	metadata?: Metadata[],
	unitMetadata?: UnitMetadata,
) => {
	if (metadata && unitMetadata) {
		const unit = unitMetadata?.unit;
		if (unitMetadata.unique) {
			return getUniqueAttributeLabel(metadata);
		} else {
			const precision = unitMetadata.precision ?? 0;
			const keys = metadata.map(({ key }) => {
				return getActionLabelAndQuery(
					key,
					range,
					unit,
					precision,
					t,
					getSystemValueFormatted,
				).label;
			});

			return keys.join(', ');
		}
	} else {
		return '';
	}
};

export const getUniqueAttributeLabel = (metadata?: Metadata[]) => {
	if (metadata) {
		const keys: string[] = [];

		metadata.forEach(({ key, excluded, group }) => {
			if (!excluded) {
				if (group) keys.push('(' + key + ')');
				else keys.push(key);
			}
		});

		return keys.join(', ');
	} else {
		return '';
	}
};

const getOperatorText = (operator: string, t: TFunc) => {
	switch (operator) {
		case '=':
			return t('EQ');
		case '<=':
			return t('LE');
		case '>':
			return t('GT');
		default:
			return operator;
	}
};

export const getGroupedActionLabelAndQuery = (
	metadata: Metadata,
): { label: string; label2: string; query: string[] } => {
	// So we need to go though each of the nested metadata to create a mother query
	const parts: string[] = [];
	metadata.group?.forEach(group => {
		parts.push(group.key);
	});

	const label = parts.join(', ');

	return { label: label, label2: label, query: parts };
};

export const getActionLabelAndQuery = (
	metadataKey: string,
	range: boolean,
	unit: string | undefined,
	precision: number,
	t: TFunc,
	getSystemValueFormatted: (
		value: string,
		invalidValue?: string,
		decimalPlaces?: number,
		standardUnit?: string,
	) => string | undefined,
): { label: string; label2: string; query: string[] } => {
	let label = metadataKey;
	let label2 = metadataKey;
	let parts;

	if (range) {
		unit = unit?.length !== 0 ? unit : undefined;
		parts = metadataKey.trim().split(' ');

		if (parts.length == 2) {
			label = `${parts[0]} ${getSystemValueFormatted(
				`${parts[1]} ${unit}`,
				parts[1],
				precision,
			)}`;

			label2 = `${getOperatorText(parts[0], t)} ${getSystemValueFormatted(
				`${parts[1]} ${unit}`,
				parts[1],
				precision,
			)}`.replaceAll(',', '');
		} else if (parts.length == 3) {
			label = `${getSystemValueFormatted(
				`${parts[0]} ${unit}`,
				parts[0],
				precision,
			)} ${parts[1]} ${getSystemValueFormatted(
				`${parts[2]} ${unit}`,
				parts[2],
				precision,
			)}`;

			label2 = `${getSystemValueFormatted(
				`${parts[0]} ${unit}`,
				parts[0],
				precision,
			)} ${getOperatorText(parts[1], t)} ${getSystemValueFormatted(
				`${parts[2]} ${unit}`,
				parts[2],
				precision,
			)}`.replaceAll(',', '');
		} else {
			label2 = `${getOperatorText('=', t)} ${label2}`;
		}
	} else {
		parts = [metadataKey.trim()];
		label2 = `${label2}`; //${getOperatorText('=', t)} ${label2}`;
	}

	return { label: label, label2: label2, query: parts };
};

export const createAction = (
	label: string,
	notes: string,
	treeId: string,
): RehabAction => {
	const newAction: RehabAction = {
		_id: uuid.v4(),
		actionId: label,
		treeId: treeId,
		unitCost: 0,
		costUnit: 'USD', //"USD" or "GBP", now is not used
		unit: CostUnit.length,
		settings: {
			enabled: true,
			defects: [],
		},
		notes: notes,
	};

	return newAction;
};

export const createConnector = (
	source: string,
	target: string,
	startNode: boolean,
	trueConnector: boolean,
) => {
	const newConnector = {
		id: `${startNode ? '' : 'e'}${source}_${target}`,
		source: source,
		target: target,
		label: startNode ? '' : trueConnector ? 'True' : 'False',
	};
	return newConnector;
};

export const createNodeAction = (
	parentId: string,
	label: string,
	notes: string,
	actionId: string,
	treeId: string,
	position: Position,
	trueAction?: boolean,
) => {
	const nodeId = 'idx_' + uuid.v4().replaceAll('-', ''); //new Date().getTime().toString(); THIS HAS ISSUES AS IT JUST GIVES THE SAME TIME
	const newNode = {
		id: nodeId,
		parentId: parentId,
		data: {
			label: label,
			nodeData: {
				title: label,
				actionId: actionId,
				treeId: treeId,
				unitCost: 0,
				costUnit: 'USD',
				unit: CostUnit.length,
				notes: notes,
			},
		},
		connectorCondition: trueAction ? 'True' : 'False',
		type: NODE_TYPES.action,
		position: position,
	};

	return newNode;
};

export const createNodeQuery = (
	parentId: string,
	range: boolean,
	label: string,
	field: string,
	value: string[],
	type: string | undefined,
	unit: string | undefined,
	position: Position,
) => {
	const nodeId = 'idx_' + uuid.v4().replaceAll('-', ''); //new Date().getTime().toString(); THIS HAS ISSUES AS IT JUST GIVES THE SAME TIME

	const rules: Rules[] = [];
	if (range) {
		let yearOnly = false;
		let date = false;

		// TODO - THIS NEEDS UPDATING WHEN WE USE THE lib-asset-schema CORRECTLY
		if (field === 'YEAR_LAID') {
			yearOnly = true;
		} else if (type === 'date') {
			date = true;
		}

		if (value.length === 2) {
			const values = [] as any;
			// Adjust from a year to a date string
			if (yearOnly) {
				values.push(`${value[1]}-02-02T12:00:00.000Z`);
			} else if (date) {
				values.push(`${value[1]}-12-31T23:59:59.999Z`);
			} else {
				values.push(value[1]);
			}

			if (value[0] === '>') {
				rules.push({
					id: uuid.v4(),
					recordType: 'Asset attributes',
					config: '',
					field: field.toUpperCase(),
					operator: yearOnly ? 'AFTER' : 'GREATER_THAN',
					value1: values,
					value2: '',
					unitLength: '',
					unit: unit,
					filters: [],
				});
			} else {
				rules.push(
					{
						id: uuid.v4(),
						recordType: 'Asset attributes',
						config: '',
						field: field.toUpperCase(),
						operator: yearOnly ? 'BEFORE' : 'LESS_THAN',
						value1: values,
						value2: '',
						unitLength: '',
						unit: unit,
						filters: [],
					},
					{
						id: uuid.v4(),
						recordType: 'Asset attributes',
						config: '',
						field: field.toUpperCase(),
						operator: 'EQUALS',
						value1: values,
						value2: '',
						unitLength: '',
						ruleCondition: 'OR',
						unit: unit,
						filters: [],
					},
				);
			}
		} else if (value.length === 3) {
			const values = [] as any;
			// Adjust from a year to a date string
			if (yearOnly) {
				values.push(`${value[2]}-02-02T12:00:00.000Z`);
			} else if (date) {
				values.push(`${value[2]}-12-31T23:59:59.999Z`);
			} else {
				values.push(value[2]);
			}

			rules.push(
				{
					id: uuid.v4(),
					recordType: 'Asset attributes',
					config: '',
					field: field.toUpperCase(),
					operator: yearOnly ? 'BEFORE' : 'LESS_THAN',
					value1: values,
					value2: '',
					unitLength: '',
					unit: unit,
					filters: [],
				},
				{
					id: uuid.v4(),
					recordType: 'Asset attributes',
					config: '',
					field: field.toUpperCase(),
					operator: 'EQUALS',
					value1: values,
					value2: '',
					unitLength: '',
					ruleCondition: 'OR',
					unit: unit,
					filters: [],
				},
			);
		}
	} else {
		rules.push({
			id: uuid.v4(),
			recordType: 'Asset attributes',
			config: '',
			field: field.toUpperCase(),
			operator: 'EQUALS',
			value1: value,
			value2: '',
			unitLength: '',
			unit: '',
			filters: [],
		});
	}

	const newNode = {
		id: nodeId,
		parentId: parentId,
		data: {
			label: label,
			nodeData: {
				title: label,
				rules: rules,
			},
		},
		type: NODE_TYPES.query,
		position: position,
	};

	return newNode;
};

export const parseAttributes = (
	treeId: string,
	index: number,
	allAtributes: AttributeOption[],
	parentMetadata: Metadata[],
	parentNodeId: string,
	parentLabel: string,
	position: Position,
	falloutAction: RehabAction,
	treeNodes: ReactFlowSchema[],
	actions: RehabAction[],
	t: TFunc,
	getSystemValueFormatted: (
		value: string,
		invalidValue?: string,
		decimalPlaces?: number,
		standardUnit?: string,
	) => string | undefined,
): Position => {
	let newQuery;
	let actionAndQuery;
	let fromId = parentNodeId;
	let newX = position.x;
	let newY = position.y + yOffset;

	// Are we at the bottom of the branch?
	if (allAtributes.length - 1 == index) {
		const attributeKey = allAtributes[index].key;
		const attributeDBField = allAtributes[index].dbFieldName;
		const attributeLabel = allAtributes[index].value;
		const config = allAtributes[index].config;
		if (config) {
			const range = config.metadataType === 'range';
			let firstQuery = true;
			// Create the query and action
			config.metadata?.forEach(metadata => {
				if (metadata.key !== '--' && !metadata.excluded) {
					const type = config.unitMetadata?.type;
					const unit = config.unitMetadata?.unit;
					const precision = config.unitMetadata?.precision ?? 0;

					if (metadata.group) {
						actionAndQuery =
							getGroupedActionLabelAndQuery(metadata);
					} else {
						actionAndQuery = getActionLabelAndQuery(
							metadata.key,
							range,
							unit,
							precision,
							t,
							getSystemValueFormatted,
						);
					}

					// The Query
					newQuery = createNodeQuery(
						fromId,
						range,
						`${attributeLabel} ${actionAndQuery.label}`,
						attributeDBField ?? attributeKey,
						actionAndQuery.query,
						type,
						unit,
						{
							x: newX,
							y: newY,
						},
					);
					treeNodes.push(newQuery);

					// The connector from the parent query to the new query
					const newParentConnector = createConnector(
						fromId,
						newQuery.id,
						treeNodes.length == 2,
						firstQuery,
					);
					treeNodes.push(newParentConnector);
					firstQuery = false;

					// The Action
					const label =
						parentLabel === ''
							? actionAndQuery.label2
							: `${parentLabel}, ${actionAndQuery.label2}`;
					const newAction = createAction(label, '', treeId);
					actions.push(newAction);

					// Now Action Node
					const newActionNode = createNodeAction(
						newQuery.id,
						label,
						'',
						newAction._id,
						treeId,
						{
							x: newX, // - (xOffset - xLeftOffset),
							y: newY + yOffset,
						},
						true,
					);
					treeNodes.push(newActionNode);

					// Finally the connector from the query to the action
					const newConnector = createConnector(
						newQuery.id,
						newActionNode.id,
						false,
						true,
					);
					treeNodes.push(newConnector);

					// Update the iteration variables
					newX += xOffset;
					newY += yOffset;
					fromId = newQuery.id;
				}
			});
		}

		// Finally the fall out node
		const newActionNode = createNodeAction(
			fromId,
			falloutAction.actionId,
			'',
			falloutAction._id,
			treeId,
			{ x: newX, y: newY },
			false,
		);
		treeNodes.push(newActionNode);

		// Finally the connector from the query to the fall out action
		const newConnector = createConnector(
			fromId,
			newActionNode.id,
			false,
			false,
		);
		treeNodes.push(newConnector);

		return { x: newX, y: newY };
	} else {
		// Go deeper
		const attributeKey = allAtributes[index].key;
		const attributeDBField = allAtributes[index].dbFieldName;
		const attributeLabel = allAtributes[index].value;
		const config = allAtributes[index].config;
		if (config && config.metadata) {
			const range = config.metadataType === 'range';
			let falloutX = position.x;
			let firstQuery = true;
			config.metadata.forEach(metadata => {
				if (metadata.key !== '--' && !metadata.excluded) {
					const type = config.unitMetadata?.type;
					const unit = config.unitMetadata?.unit;
					const precision = config.unitMetadata?.precision ?? 0;

					if (metadata.group) {
						actionAndQuery =
							getGroupedActionLabelAndQuery(metadata);
					} else {
						actionAndQuery = getActionLabelAndQuery(
							metadata.key,
							range,
							unit,
							precision,
							t,
							getSystemValueFormatted,
						);
					}

					// The Query
					newQuery = createNodeQuery(
						fromId,
						range,
						`${attributeLabel} ${actionAndQuery.label}`,
						attributeDBField ?? attributeKey,
						actionAndQuery.query,
						type,
						unit,
						{
							x: newX,
							y: newY,
						},
					);
					treeNodes.push(newQuery);
					// Make the fallout node next to the last query quild to avoid a huge gap
					falloutX = newX + xOffset;

					// The connector from the parent query to the new query
					const newParentConnector = createConnector(
						fromId,
						newQuery.id,
						treeNodes.length == 2,
						firstQuery,
					);
					treeNodes.push(newParentConnector);
					firstQuery = false;

					const lastPosition = parseAttributes(
						treeId,
						index + 1,
						allAtributes,
						[...parentMetadata, metadata],
						newQuery.id,
						parentLabel === ''
							? actionAndQuery.label2
							: `${parentLabel}, ${actionAndQuery.label2}`,
						{ x: newX, y: newY },
						falloutAction,
						treeNodes,
						actions,
						t,
						getSystemValueFormatted,
					);

					newX = lastPosition.x;
					newY += yOffset;
					fromId = newQuery.id;
				}
			});

			// Finally the fall out node
			const newActionNode = createNodeAction(
				fromId,
				falloutAction.actionId,
				'',
				falloutAction._id,
				treeId,
				{
					x: falloutX,
					y: newY,
				},
				false,
			);
			treeNodes.push(newActionNode);

			// Finally the connector from the query to the fall out action
			const newConnector = createConnector(
				fromId,
				newActionNode.id,
				false,
				false,
			);
			treeNodes.push(newConnector);
		}

		return { x: newX, y: newY };
	}
};

export const buildTreeNodeActions = (
	treeId: string,
	allAtributes: AttributeOption[],
	t: TFunc,
	getSystemValueFormatted: (
		value: string,
		invalidValue?: string,
		decimalPlaces?: number,
		standardUnit?: string,
	) => string | undefined,
): {
	treeNodes: ReactFlowSchema[];
	actions: RehabAction[];
	startNodeId: string;
} => {
	const treeNodes: ReactFlowSchema[] = [];
	const actions: RehabAction[] = [];

	const startNode = createStartNode(xStart, yStart);
	treeNodes.push(startNode);

	const falloutAction = createAction(t('Everything Else'), '', treeId);
	actions.push(falloutAction);

	parseAttributes(
		treeId,
		0,
		allAtributes,
		[],
		startNode.id,
		'',
		{ x: xStart, y: yStart },
		falloutAction,
		treeNodes,
		actions,
		t,
		getSystemValueFormatted,
	);

	return {
		treeNodes: treeNodes,
		actions: actions,
		startNodeId: startNode.id,
	};
};
